import { forwardRef, useMemo, useState } from 'react'
import { Popover, PopoverBody } from 'reactstrap'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import _ from 'lodash'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import Select from '../../common/form/Select'
import CheckBox from '../../common/form/CheckBox'
import { ReactComponent as FilterIcon } from '../../../assets/images/common/icons/filter-icon.svg'
import { ReactComponent as DropDownIcon } from '../../../assets/images/common/icons/select-drop-down-icon-light-navy.svg'

import {
  getEnrollmentStatusOptions,
  getPaymentStatusOptions,
} from '../../../utility/enrollmentsBilling'

import styles from './EnrollmentsFilterBar.module.scss'

const DEFAULT_FILTERS = {
  enrollmentStatus: [],
  invoiceStatus: [],
  courseId: null,
  packageId: null,
  dateJoinedStart: null,
  dateJoinedEnd: null,
}

// eslint-disable-next-line react/prop-types
const DatePickerInput = forwardRef(({ value, onClick }, ref) => {
  const { t } = useTranslation()

  return (
    <button className={styles.datePickerInput} onClick={onClick} ref={ref}>
      {value || t('enrollmentsBilling.filterBar.selectDate')}
      <DropDownIcon />
    </button>
  )
})

DatePickerInput.displayName = 'DatePickerInput'

const FilterSection = ({ title, isOpen, onToggle, children }) => (
  <div className={styles.filterSection}>
    <button type='button' className={styles.filterSectionHeader} onClick={onToggle}>
      <span className={styles.filterName}>{title}</span>
      <DropDownIcon className={classNames(styles.sectionChevron, { [styles.sectionChevronOpen]: isOpen })} />
    </button>
    {isOpen && <div className={styles.filterSectionBody}>{children}</div>}
  </div>
)

FilterSection.propTypes = {
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node,
}

const SECTION_KEYS = ['enrollmentStatus', 'paymentStatus', 'programs', 'packages', 'dateJoined']

const EnrollmentsFilterBar = ({ appliedFilters, onApplyFilters, programOptions, packageOptions, hideProgramFilter = false }) => {
  const { t } = useTranslation()
  const enrollmentStatusOptions = getEnrollmentStatusOptions(t)
  const paymentStatusOptions = getPaymentStatusOptions(t)
  const buttonId = useMemo(() => _.uniqueId('enrollment-filter-'), [])
  const [isOpen, setIsOpen] = useState(false)
  const [editFilters, setEditFilters] = useState(appliedFilters)
  const [openSections, setOpenSections] = useState(
    () => SECTION_KEYS.reduce((acc, key) => ({ ...acc, [key]: true }), {})
  )

  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }))

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (appliedFilters.enrollmentStatus?.length) { count++ }
    if (appliedFilters.invoiceStatus?.length) { count++ }
    if (appliedFilters.courseId) { count++ }
    if (appliedFilters.packageId) { count++ }
    if (appliedFilters.dateJoinedStart || appliedFilters.dateJoinedEnd) { count++ }
    return count
  }, [appliedFilters])

  const handleToggle = () => {
    if (!isOpen) {
      setEditFilters(appliedFilters)
    }
    setIsOpen(prev => !prev)
  }

  const handleApply = () => {
    onApplyFilters(editFilters)
    setIsOpen(false)
  }

  const handleClear = () => {
    onApplyFilters(DEFAULT_FILTERS)
    setIsOpen(false)
  }

  const toggleCheckbox = (filterKey, value) => {
    setEditFilters(prev => {
      const current = prev[filterKey] || []
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value]
      return { ...prev, [filterKey]: updated }
    })
  }

  return (
    <>
      <button
        id={buttonId}
        className={classNames(styles.filtersButton, { [styles.active]: activeFilterCount > 0 })}
      >
        <FilterIcon className='mx-2' />
        {activeFilterCount > 0
          ? t('enrollmentsBilling.filterBar.filtersApplied', { count: activeFilterCount })
          : t('enrollmentsBilling.filterBar.filters')}
        {activeFilterCount > 0 && (
          <button
            className={styles.clearFiltersButton}
            onClick={(e) => { e.stopPropagation(); handleClear() }}
          >
            <i className='fa fa-close' />
          </button>
        )}
      </button>

      <Popover
        target={buttonId}
        isOpen={isOpen}
        toggle={handleToggle}
        trigger='legacy'
        placement='bottom-start'
        popperClassName={styles.filtersMenu}
      >
        <PopoverBody className='p-3'>
          <div className={styles.filterHeader}>
            <p className={styles.filterTitle}>{t('enrollmentsBilling.filterBar.filters')}</p>
            <button className={styles.clearAllButton} onClick={handleClear}>{t('enrollmentsBilling.filterBar.clearAll')}</button>
          </div>

          <FilterSection
            title={t('enrollmentsBilling.filterBar.enrollmentStatus')}
            isOpen={openSections.enrollmentStatus}
            onToggle={() => toggleSection('enrollmentStatus')}
          >
            <div className={styles.checkboxList}>
              {enrollmentStatusOptions.map(option => (
                <CheckBox
                  key={option.value}
                  id={`enrollment-status-${option.value}`}
                  isSelected={editFilters.enrollmentStatus?.includes(option.value)}
                  onClick={() => toggleCheckbox('enrollmentStatus', option.value)}
                  label={option.label}
                />
              ))}
            </div>
          </FilterSection>

          <FilterSection
            title={t('enrollmentsBilling.filterBar.paymentStatus')}
            isOpen={openSections.paymentStatus}
            onToggle={() => toggleSection('paymentStatus')}
          >
            <div className={styles.checkboxList}>
              {paymentStatusOptions.map(option => (
                <CheckBox
                  key={option.value}
                  id={`payment-status-${option.value}`}
                  isSelected={editFilters.invoiceStatus?.includes(option.value)}
                  onClick={() => toggleCheckbox('invoiceStatus', option.value)}
                  label={option.label}
                />
              ))}
            </div>
          </FilterSection>

          {!hideProgramFilter && (
            <FilterSection
              title={t('enrollmentsBilling.filterBar.programs')}
              isOpen={openSections.programs}
              onToggle={() => toggleSection('programs')}
            >
              <Select
                isClearable
                options={programOptions}
                value={programOptions.find(o => o.value === editFilters.courseId) || null}
                onChange={(selected) => setEditFilters(prev => ({
                  ...prev,
                  courseId: selected?.value || null,
                }))}
                placeholder={t('enrollmentsBilling.filterBar.choosePrograms')}
                classNames={{ menuList: () => styles.selectMenuList }}
              />
            </FilterSection>
          )}

          <FilterSection
            title={t('enrollmentsBilling.filterBar.packages')}
            isOpen={openSections.packages}
            onToggle={() => toggleSection('packages')}
          >
            <Select
              isClearable
              options={packageOptions}
              value={packageOptions?.find(o => o.value === editFilters.packageId) || null}
              onChange={(selected) => setEditFilters(prev => ({
                ...prev,
                packageId: selected?.value || null,
              }))}
              placeholder={t('enrollmentsBilling.filterBar.choosePackages')}
              classNames={{ menuList: () => styles.selectMenuList }}
            />
          </FilterSection>

          <FilterSection
            title={t('enrollmentsBilling.filterBar.dateJoined')}
            isOpen={openSections.dateJoined}
            onToggle={() => toggleSection('dateJoined')}
          >
            <p className={styles.sectionDescription}>{t('enrollmentsBilling.filterBar.dateJoinedDescription')}</p>
            <div className={styles.dateRange}>
              <div className={styles.dateField}>
                <span className={styles.dateLabel}>{t('enrollmentsBilling.filterBar.from')}</span>
                <DatePicker
                  selected={editFilters.dateJoinedStart ? new Date(editFilters.dateJoinedStart) : null}
                  customInput={<DatePickerInput />}
                  onChange={(date) => setEditFilters(prev => ({
                    ...prev,
                    dateJoinedStart: date ? moment(date).format('YYYY-MM-DD') : null,
                  }))}
                  dateFormat='MMM d, yyyy'
                  isClearable
                />
              </div>
              <div className={styles.dateField}>
                <span className={styles.dateLabel}>{t('enrollmentsBilling.filterBar.to')}</span>
                <DatePicker
                  selected={editFilters.dateJoinedEnd ? new Date(editFilters.dateJoinedEnd) : null}
                  customInput={<DatePickerInput />}
                  onChange={(date) => setEditFilters(prev => ({
                    ...prev,
                    dateJoinedEnd: date ? moment(date).format('YYYY-MM-DD') : null,
                  }))}
                  dateFormat='MMM d, yyyy'
                  isClearable
                />
              </div>
            </div>
          </FilterSection>

          <div className={styles.applyWrapper}>
            <button className={styles.applyButton} onClick={handleApply}>{t('enrollmentsBilling.filterBar.apply')}</button>
          </div>
        </PopoverBody>
      </Popover>
    </>
  )
}

EnrollmentsFilterBar.propTypes = {
  appliedFilters: PropTypes.object.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  hideProgramFilter: PropTypes.bool,
  programOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any,
  })),
  packageOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any,
  })),
}

export { DEFAULT_FILTERS }
export default EnrollmentsFilterBar
